import { motion } from 'framer-motion';
import { useLanguage } from '../../lib/i18n/LanguageContext';
import {
  generateAltText,
  optimizePexelsUrl,
  generatePexelsSrcSet,
} from '../../lib/utils/imageOptimization';
import SectionTitle from '../../components/ui/SectionTitle';

const CareersWhyJoin = () => {
  const { t } = useLanguage();

  return (
    <section className="py-20 bg-white">
      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <SectionTitle title={t.careers.whyJoin.title} align="left" className="mb-8">
              <p className="text-neutral-700 mt-4">{t.careers.whyJoin.subtitle}</p>
            </SectionTitle>

            <div className="space-y-6">
              {t.careers.whyJoin.benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="bg-red-100 p-3 rounded-full text-red-800">
                    {index === 0 && (
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M16.24 7.76001L14.12 14.12L7.76001 16.24L9.88001 9.88001L16.24 7.76001Z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                    {index === 1 && (
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13M16 3.13C16.8604 3.3503 17.623 3.8507 18.1676 4.55231C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89317 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88M13 7C13 9.20914 11.2091 11 9 11C6.79086 11 5 9.20914 5 7C5 4.79086 6.79086 3 9 3C11.2091 3 13 4.79086 13 7Z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                    {index === 2 && (
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M20 7H4C2.89543 7 2 7.89543 2 9V19C2 20.1046 2.89543 21 4 21H20C21.1046 21 22 20.1046 22 19V9C22 7.89543 21.1046 7 20 7Z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M16 21V5C16 4.46957 15.7893 3.96086 15.4142 3.58579C15.0391 3.21071 14.5304 3 14 3H10C9.46957 3 8.96086 3.21071 8.58579 3.58579C8.21071 3.96086 8 4.46957 8 5V21"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                    {index === 3 && (
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M18.7273 14.7273C18.6063 15.0909 18.6273 15.4909 18.7891 15.8364C18.9509 16.1818 19.2418 16.4455 19.6 16.5818L19.9091 16.7273C20.3172 16.8819 20.6389 17.1899 20.8145 17.5864C20.99 17.9829 21.0076 18.4363 20.8636 18.8455C20.6046 19.6255 20.28 20.3777 19.8909 21.0909C19.6739 21.4756 19.3171 21.7714 18.8916 21.9198C18.4661 22.0682 18.0023 22.0591 17.5818 21.8945L17.2818 21.7636C16.9259 21.6146 16.5289 21.598 16.1624 21.7182C15.7959 21.8384 15.4864 22.0859 15.2909 22.4091C15.1603 22.62 15.0645 22.8491 15.0055 23.0891C14.9117 23.4718 14.6699 23.8082 14.3282 24.0303C13.9865 24.2525 13.5739 24.3427 13.1727 24.2818C12.3909 24.1755 11.6218 24.1755 10.84 24.2818C10.4388 24.3427 10.0262 24.2525 9.68449 24.0303C9.34279 23.8082 9.10102 23.4718 9.00727 23.0891C8.94668 22.8399 8.84561 22.6021 8.70909 22.3855C8.51357 22.0623 8.20407 21.8147 7.83758 21.6945C7.47108 21.5743 7.07411 21.5909 6.71818 21.74L6.41818 21.8709C5.99763 22.0355 5.53382 22.0446 5.10832 21.8962C4.68282 21.7478 4.32607 21.4519 4.10909 21.0673C3.72 20.3541 3.39535 19.6019 3.13636 18.8218C2.99237 18.4126 3.01003 17.9592 3.18553 17.5627C3.36103 17.1662 3.68279 16.8582 4.09091 16.7036L4.4 16.5582C4.75821 16.4218 5.04911 16.1582 5.21089 15.8127C5.37266 15.4673 5.39366 15.0673 5.27273 14.7036C5.20389 14.4794 5.16551 14.2483 5.15909 14.0164C5.15909 13.7782 5.20909 13.5345 5.27273 13.2964C5.39366 12.9327 5.37266 12.5327 5.21089 12.1873C5.04911 11.8418 4.75821 11.5782 4.4 11.4418L4.09091 11.2964C3.68279 11.1418 3.36103 10.8338 3.18553 10.4373C3.01003 10.0408 2.99237 9.5874 3.13636 9.17818C3.39535 8.39813 3.72 7.64591 4.10909 6.93273C4.32607 6.54809 4.68282 6.2522 5.10832 6.10381C5.53382 5.95543 5.99763 5.96454 6.41818 6.12909L6.71818 6.26C7.07411 6.40905 7.47108 6.42565 7.83758 6.30544C8.20407 6.18524 8.51357 5.93773 8.70909 5.61455C8.83967 5.4036 8.93544 5.1745 8.99455 4.93455C9.0883 4.55176 9.33007 4.21536 9.67177 3.9932C10.0135 3.77103 10.4261 3.6809 10.8273 3.74182C11.6091 3.84813 12.3782 3.84813 13.16 3.74182C13.5612 3.6809 13.9738 3.77103 14.3155 3.9932C14.6572 4.21536 14.899 4.55176 14.9927 4.93455C15.0533 5.1838 15.1544 5.42157 15.2909 5.63818C15.4864 5.96137 15.7959 6.20888 16.1624 6.32908C16.5289 6.44928 16.9259 6.43268 17.2818 6.28364L17.5818 6.15273C18.0023 5.98817 18.4661 5.97907 18.8916 6.12745C19.3171 6.27583 19.6739 6.57173 19.8909 6.95636C20.28 7.66955 20.6046 8.42177 20.8636 9.20182C21.0076 9.61104 20.99 10.0644 20.8145 10.4609C20.6389 10.8574 20.3172 11.1654 19.9091 11.32L19.6 11.4655C19.2418 11.6018 18.9509 11.8655 18.7891 12.2109C18.6273 12.5564 18.6063 12.9564 18.7273 13.32C18.7961 13.5442 18.8345 13.7752 18.8409 14.0073C18.8409 14.2455 18.7909 14.4891 18.7273 14.7273Z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                  <div>
                    <h3 className="font-serif font-semibold text-lg text-neutral-900 mb-1">
                      {benefit.title}
                    </h3>
                    <p className="text-neutral-700">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <img
              src={optimizePexelsUrl(
                'https://images.pexels.com/photos/175753/pexels-photo-175753.jpeg',
                1200,
                800
              )}
              srcSet={generatePexelsSrcSet(
                'https://images.pexels.com/photos/175753/pexels-photo-175753.jpeg'
              )}
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 40vw"
              alt={generateAltText('staff', 'Professional kitchen team at work')}
              title="Join our passionate team at Rossopomodoro Reykjavik"
              loading="lazy"
              width="1200"
              height="800"
              className="rounded-lg shadow-lg w-full h-auto"
            />
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-t from-black/30 to-transparent rounded-lg"></div>
            <div className="absolute bottom-6 left-6 right-6 text-white">
              <h3 className="font-serif text-2xl font-semibold mb-2">
                {t.careers.application.joinFamily}
              </h3>
              <p>{t.careers.application.joinFamilyDescription}</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CareersWhyJoin;
